import { components } from "@certn/api/generated-schemas/client-portal";
import { REQUESTOR_ROLE_CODE } from "@certn/client-users";
import { RegionContext } from "@certn/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext } from "react";

import { usePermissions } from "./PermissionsProvider";
import { useUser } from "../../hooks/useUser";

type Permission = components["schemas"]["LoggedInUserPermissionsList"];

export const userPagePermissions = [
  {
    action: "activation",
    target: "user",
    scope: "all",
  },
  {
    action: "change_role",
    target: "user",
    scope: "all",
  },
  {
    action: "create",
    target: "user",
    scope: "all",
  },
  {
    action: "read",
    target: "user",
    scope: "all",
  },
  {
    action: "update",
    target: "user",
    scope: "all",
  },
] as unknown as Permission;
export const checkArgumentsTemplatePermissions = [
  {
    action: "create",
    target: "check_arguments_template",
    scope: "all",
  },
  {
    action: "read",
    target: "check_arguments_template",
    scope: "all",
  },
  {
    action: "update",
    target: "check_arguments_template",
    scope: "all",
  },
] as unknown as Permission;

export const brandingPagePermissions = {
  action: "read",
  target: "branding",
  scope: "all",
} as unknown as Permission;

export function useCanViewBranding() {
  const { hasPermission } = usePermissions();

  return hasPermission(brandingPagePermissions);
}

export function useCanAccessSettings() {
  const { hasPermission } = usePermissions();

  return (
    hasPermission(userPagePermissions) ||
    hasPermission({
      action: "read",
      target: "tag",
      scope: "all",
    }) ||
    hasPermission({
      action: "configure",
      target: "public_api",
      scope: "all",
    }) ||
    hasPermission({
      action: "read",
      target: "check_settings",
      scope: "all",
    }) ||
    hasPermission(checkArgumentsTemplatePermissions) ||
    hasPermission({
      action: "read",
      target: "questionnaire",
      scope: "all",
    }) ||
    hasPermission({
      action: "configure",
      target: "data_retention",
      scope: "all",
    }) ||
    hasPermission(brandingPagePermissions)
  );
}

export function useCanViewGroups() {
  const { hasPermission } = usePermissions();

  return (
    hasPermission({
      action: "read",
      target: "customer_group",
      scope: "all",
    }) ||
    hasPermission({
      action: "read",
      target: "customer_group",
      scope: "my_groups",
    })
  );
}

export function useCanUpdateGroups() {
  const { hasPermission } = usePermissions();

  return (
    hasPermission({
      action: "update",
      target: "customer_group",
      scope: "all",
    }) ||
    hasPermission({
      action: "update",
      target: "customer_group",
      scope: "my_groups",
    })
  );
}

export function useCanCreateGroups() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "create",
    target: "customer_group",
    scope: "all",
  });
}

export function useCanCreatePackages() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "create",
    target: "package",
    scope: "all",
  });
}

export function useCanViewProfiles() {
  const { hasPermission } = usePermissions();

  return (
    hasPermission({
      action: "read",
      target: "subject",
      scope: "all",
    }) &&
    (hasPermission({
      action: "read",
      target: "case",
      scope: "all",
    }) ||
      hasPermission({
        action: "read",
        target: "case",
        scope: "my_groups",
      }))
  );
}

export function useCanViewBillingProfiles() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "read",
    target: "billing_profile",
    scope: "all",
  });
}

export function useCanCreateApplyLinks() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "create",
    target: "apply_link",
    scope: "all",
  });
}

export function useCanViewAdjudication() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "read",
    target: "adjudication_rubric",
    scope: "all",
  });
}

export function useCanViewClientPortalPricing() {
  const { hasPermission } = usePermissions();

  return hasPermission({
    action: "read",
    target: "client_portal_pricing",
    scope: "all",
  });
}

// Mirrors the ACCOUNT section ordering in SettingsNavigation.tsx / the route
// gating in SettingsRoutes.tsx, so the Settings nav item never lands on a
// route that isn't registered for the current user. Falls back to a single
// TEMPLATES route (unaffected by feature flags in SettingsRoutes.tsx).
export function useDefaultSettingsRoute() {
  const { hasPermission } = usePermissions();
  const { details } = useUser();
  const {
    clientPortalUserManagement,
    clientPortalOrderNonBiometricIdCanada,
    clientPortalQuestionnaireTemplates,
    mfaEnabled,
    enableDataRetentionSettings,
    enableCobrandingForApplications,
  } = useFlags();
  const region = useContext(RegionContext);
  const canViewGroups = useCanViewGroups();
  const canViewBranding = useCanViewBranding();
  const canAccessSettings = useCanAccessSettings();
  const showDataRetention = enableDataRetentionSettings && region !== "au";
  const hasTenacyPermissablePurpose =
    details?.customer_groups?.some(
      (group) => group.permissible_purpose === "TENANCY"
    ) || false;

  const settingsRoutePriority = [
    {
      isAccessible:
        canViewGroups &&
        clientPortalUserManagement &&
        hasPermission(userPagePermissions),
      path: "/settings/users",
    },
    {
      isAccessible: hasPermission({
        action: "read",
        target: "tag",
        scope: "all",
      }),
      path: "/settings/tags",
    },
    {
      isAccessible: enableCobrandingForApplications && canViewBranding,
      path: "/settings/branding",
    },
    {
      isAccessible: hasPermission({
        action: "configure",
        target: "public_api",
        scope: "all",
      }),
      path: "/settings/integrations",
    },
    {
      isAccessible: (mfaEnabled || showDataRetention) && canAccessSettings,
      path: "/settings/security",
    },
    {
      isAccessible:
        clientPortalOrderNonBiometricIdCanada &&
        hasPermission({
          action: "read",
          target: "check_settings",
          scope: "all",
        }),
      path: "/settings/check-settings",
    },
    {
      isAccessible:
        !!clientPortalQuestionnaireTemplates &&
        hasTenacyPermissablePurpose &&
        hasPermission({
          action: "read",
          target: "questionnaire",
          scope: "all",
        }),
      path: "/settings/questionnaire-templates",
    },
    {
      isAccessible: hasPermission(checkArgumentsTemplatePermissions),
      path: "/settings/reference-check-templates",
    },
  ];

  return (
    settingsRoutePriority.find((route) => route.isAccessible)?.path ||
    "/settings/reference-check-templates"
  );
}

// Temporary Requestor gate for settings / case score / case-details navigation.
// useIsRequestor is false while roles are still loading, so the hooks below return true briefly; backend authz still applies.
// TODO: AUSPRO-682 — use /me permission tuples + hasPermission (fail-closed while loading).
function useIsRequestor() {
  const { details } = useUser();
  const roles = details?.roles || [];
  return roles.some((role) => role.code === REQUESTOR_ROLE_CODE);
}

export function useCanViewSettings() {
  return !useIsRequestor();
}

export function useCanViewCaseScore() {
  return !useIsRequestor();
}

export function useCanViewCaseDetails() {
  return !useIsRequestor();
}
